import { Button } from "components/Button";
import { DotLoading } from "components/DotLoading";
import {
  DashboardResponseV3,
  DashboardSubOriginV3,
} from "services/dashboardService";
import { cn } from "utils/styles";
import { LeadsResume } from "./components/LeadsResume";
import { PlatformRow } from "./components/PlatformRow";
import { SalesResume } from "./components/SalesResume";
import { useViewmodel } from "./viewModel";

interface Props {
  showConfigModal: () => void;
}

const SUB_ORIGIN_PLATAFORMS: Array<{
  key: DashboardSubOriginV3;
  plataform:
    | "organic_direct"
    | "panfleto"
    | "nao_atribuido"
    | "indication"
    | "afiliates"
    | "reactivation"
    | "pap"
    | "condominio"
    | "outros";
}> = [
  { key: "direct", plataform: "organic_direct" },
  { key: "flyers", plataform: "panfleto" },
  { key: "unknown", plataform: "nao_atribuido" },
  { key: "indication", plataform: "indication" },
  { key: "afiliates", plataform: "afiliates" },
  { key: "reactivation", plataform: "reactivation" },
  { key: "pap", plataform: "pap" },
  { key: "condominium", plataform: "condominio" },
  { key: "others", plataform: "outros" },
];

export const Dashboard = ({ showConfigModal }: Props) => {
  const {
    hasProvider,
    dashboardData,
    dashboardVersion,
    isLoading,
    hasError,
    refetch,
    tab,
    setTab,
    t,
    selectedProvider,
  } = useViewmodel();

  if (!hasProvider) {
    return (
      <div className="w-full flex flex-col gap-6 items-center justify-center h-[calc(75svh)]">
        <p className="text-white text-xl text-center">
          {t("dashboard.no_provider")}
        </p>
      </div>
    );
  }

  if (hasError)
    return (
      <div className="w-full flex flex-col gap-6 items-center justify-center text-center h-[calc(75svh)]">
        <p className="text-accent-500 text-xl">{t("dashboard.error_title")}</p>
        <p>{t("dashboard.error_message")}</p>
        <Button onClick={refetch}>{t("actions.retry")}</Button>
      </div>
    );

  if (isLoading || !dashboardData)
    return (
      <div className="w-full flex flex-col gap-6 items-center justify-center h-[calc(75svh)]">
        <p className="text-white text-xl">{t("dashboard.loading")}</p>
        <DotLoading />
      </div>
    );

  const isV3 = dashboardVersion === "v3";
  const v3Data = isV3 ? (dashboardData as unknown as DashboardResponseV3) : null;

  return (
    <div>
      <div className="flex justify-around text-center text-xl my-3">
        <div />
        <button
          onClick={() => setTab("leads")}
          className={cn(
            "w-32 py-1.5 rounded-full",
            tab === "leads" ? "bg-primary/40" : "bg-primaryMultiply"
          )}
        >
          Leads
        </button>
        <button
          onClick={() => setTab("sales")}
          className={cn(
            "w-32 py-1.5 rounded-full",
            tab === "sales" ? "bg-primary/40" : "bg-primaryMultiply "
          )}
        >
          Vendas
        </button>
        <div />
      </div>
      {tab === "leads" ? (
        <LeadsResume data={dashboardData.total} />
      ) : (
        <SalesResume
          data={dashboardData.total}
          showConfigModal={showConfigModal}
        />
      )}
      {tab === "leads" ? (
        <div className="mt-8 flex flex-col gap-4">
          <PlatformRow plataform="meta_ads" className="">
            <LeadsResume
              variant="secondary"
              data={dashboardData.facebook}
            />
          </PlatformRow>
          <PlatformRow plataform="google_ads" className="">
            <LeadsResume
              variant="secondary"
              data={dashboardData.google}
            />
          </PlatformRow>
          {v3Data ? (
            <PlatformRow
              plataform="organic"
              className=""
              expandable
              defaultExpanded
              rows={
                <>
                  {SUB_ORIGIN_PLATAFORMS.map(({ key, plataform }) => (
                    <PlatformRow
                      key={key}
                      plataform={plataform}
                      className=""
                      indent
                    >
                      <LeadsResume
                        variant="secondary"
                        data={v3Data.organic.subOrigins[key]}
                      />
                    </PlatformRow>
                  ))}
                </>
              }
            >
              <LeadsResume
                variant="secondary"
                data={v3Data.organic}
              />
            </PlatformRow>
          ) : (
            <PlatformRow plataform="organic" className="">
              <LeadsResume
                variant="secondary"
                data={dashboardData.organic}
              />
            </PlatformRow>
          )}
          {!isV3 && selectedProvider.id === "661d3550cd0f8e73a186d120" && (
            <>
              <PlatformRow plataform="best_plan" className="">
                <LeadsResume
                  variant="secondary"
                  data={(dashboardData as any).best_plan}
                />
              </PlatformRow>
              <PlatformRow plataform="tiktok" className="">
                <LeadsResume
                  variant="secondary"
                  data={(dashboardData as any).tiktok}
                />
              </PlatformRow>
            </>
          )}
        </div>
      ) : (
        <div className="mt-8 flex flex-col gap-4">
          <PlatformRow plataform="meta_ads" className="gap-8 desktop:gap-0">
            <SalesResume
              variant="secondary"
              data={dashboardData.facebook}
              showConfigModal={showConfigModal}
            />
          </PlatformRow>
          <PlatformRow plataform="google_ads" className="gap-8 desktop:gap-0">
            <SalesResume
              variant="secondary"
              data={dashboardData.google}
              showConfigModal={showConfigModal}
            />
          </PlatformRow>
          {v3Data ? (
            <PlatformRow
              plataform="organic"
              className="gap-8 desktop:gap-0"
              expandable
              defaultExpanded
              rows={
                <>
                  {SUB_ORIGIN_PLATAFORMS.map(({ key, plataform }) => (
                    <PlatformRow
                      key={key}
                      plataform={plataform}
                      className="gap-8 desktop:gap-0"
                      indent
                    >
                      <SalesResume
                        variant="secondary"
                        data={v3Data.organic.subOrigins[key]}
                        showConfigModal={showConfigModal}
                      />
                    </PlatformRow>
                  ))}
                </>
              }
            >
              <SalesResume
                variant="secondary"
                data={v3Data.organic}
                showConfigModal={showConfigModal}
              />
            </PlatformRow>
          ) : (
            <PlatformRow plataform="organic" className="gap-8 desktop:gap-0">
              <SalesResume
                variant="secondary"
                data={dashboardData.organic}
                showConfigModal={showConfigModal}
              />
            </PlatformRow>
          )}
          {!isV3 && selectedProvider.id === "661d3550cd0f8e73a186d120" && (
            <>
              <PlatformRow plataform="best_plan" className="gap-8 desktop:gap-0">
                <SalesResume
                  variant="secondary"
                  data={(dashboardData as any).best_plan}
                  showConfigModal={showConfigModal}
                />
              </PlatformRow>
              <PlatformRow plataform="tiktok" className="gap-8 desktop:gap-0">
                <SalesResume
                  variant="secondary"
                  data={(dashboardData as any).tiktok}
                  showConfigModal={showConfigModal}
                />
              </PlatformRow>
            </>
          )}
        </div>
      )}
    </div>
  );
};
